import { createApi } from '@reduxjs/toolkit/query/react'
import { getBaseQuery, getBaseUrl, REQUEST_TIMEOUT_MS } from '../api.util.ts'
import {
  CheckIdeaExistRequest,
  CheckIdeaExistResponse,
  ClearIdeaActionAlertRequest,
  ClearIdeaAlertsRequest,
  IdeaCheckFillStrategyRequest,
  IdeaCheckFillStrategyResponse,
  IdeaCreateRequest,
  IdeaDailyPnlResponse,
  IdeaDetailResponse,
  IdeaHistoryResponse,
  IdeaLiquidateRequest,
  IdeaListRequest,
  IdeaListResponse,
  IdeaModifyResponse,
  IdeaToggleKeepFillingRequest,
  IdeaToggleKeepFillingResponse,
  IdeaUpdateRequest,
} from './idea-api.def.ts'

const interfaceName = 'idea'

export const ideaApi = createApi({
  reducerPath: 'ideaApi',
  baseQuery: getBaseQuery(getBaseUrl(interfaceName), REQUEST_TIMEOUT_MS),
  tagTypes: ['idea-list'],
  endpoints: (build) => ({
    listIdeas: build.query<IdeaListResponse, IdeaListRequest>({
      query: (params) => ({
        url: '/list',
        method: 'GET',
        params: params,
      }),
      providesTags: ['idea-list'],
    }),
    createIdea: build.mutation<IdeaModifyResponse, IdeaCreateRequest>({
      query: (payload) => ({
        url: '/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['idea-list'],
    }),
    getIdeaDetail: build.query<IdeaDetailResponse, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'GET',
      }),
      providesTags: ['idea-list'],
    }),
    checkIdeaExist: build.query<CheckIdeaExistResponse, CheckIdeaExistRequest>({
      query: (payload) => ({
        url: '/check-exist',
        method: 'POST',
        body: payload,
      }),
    }),
    getIdeaHistory: build.query<IdeaHistoryResponse, string>({
      query: (id) => ({
        url: `/${id}/history`,
        method: 'GET',
      }),
      providesTags: ['idea-list'],
    }),
    getDailyIdeaPnl: build.query<IdeaDailyPnlResponse, string>({
      query: (id) => ({
        url: `/${id}/daily-pnl`,
        method: 'GET',
      }),
      providesTags: ['idea-list'],
    }),
    updateIdea: build.mutation<IdeaModifyResponse, IdeaUpdateRequest>({
      query: (payload) => ({
        url: `/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['idea-list']),
    }),
    liquidateIdea: build.mutation<IdeaModifyResponse, IdeaLiquidateRequest>({
      query: (payload) => ({
        url: `/${payload.id}/liquidate`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: ['idea-list'],
    }),
    checkFillStrategy: build.query<
      IdeaCheckFillStrategyResponse,
      IdeaCheckFillStrategyRequest
    >({
      query: (payload) => ({
        url: `/${payload.id}/check-fill-strategy`,
        method: 'POST',
        body: payload,
      }),
    }),
    toggleKeepFilling: build.mutation<
      IdeaToggleKeepFillingResponse,
      IdeaToggleKeepFillingRequest
    >({
      query: (payload) => ({
        url: `/${payload.id}/toggle-keep-filling`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['idea-list'],
    }),
    clearIdeaAlerts: build.query<void, ClearIdeaAlertsRequest>({
      query: (payload) => ({
        url: `/${payload.id}/alert/off`,
        method: 'POST',
        body: payload,
      }),
    }),
    clearIdeaActionAlert: build.query<void, ClearIdeaActionAlertRequest>({
      query: (payload) => ({
        url: `/idea-action/${payload.actionId}/alert/off`,
        method: 'POST',
        body: payload,
      }),
    }),
  }),
})

export const {
  useCreateIdeaMutation,
  useGetIdeaDetailQuery,
  useCheckIdeaExistQuery,
  useGetIdeaHistoryQuery,
  useGetDailyIdeaPnlQuery,
  useUpdateIdeaMutation,
  useLiquidateIdeaMutation,
  useCheckFillStrategyQuery,
  useToggleKeepFillingMutation,
  useLazyClearIdeaAlertsQuery,
  useLazyClearIdeaActionAlertQuery,
} = ideaApi
